import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
	AddOrganizationAdminDocument,
	AddOrganizationDomainDocument,
	ClaimOrganizationDocument,
	CreateStripeBillingPortalSessionDocument,
	CreateStripeCheckoutSessionDocument,
	GetOrganizationAdminsDocument,
	GetOrganizationAwsMarketplaceSubscriptionDocument,
	GetOrganizationDocument,
	GetOrganizationDomainsDocument,
	GetOrganizationMetricsDocument,
	GetOrganizationSubscriptionDocument,
	LinkAwsMarketplaceSubscriptionDocument,
	OrganizationLookupByDomainDocument,
	OrganizationLookupBySlugDocument,
	RemoveOrganizationAdminDocument,
	RemoveOrganizationDomainDocument,
	RequestOrganizationDomainVerificationDocument,
	UpdateOrganizationDocument,
	VerifyOrganizationDomainDocument,
} from "@/common/artemis-sdk/graphql";
import type { Organization } from "../model";
import { useGqlClient } from "../use-gql-client";
import { useMe } from "./members";

export function useLookupOrgByDomain(domain: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["org-domain", domain],
		queryFn: () => gqlFetch(OrganizationLookupByDomainDocument, { domain }),
		enabled: !!domain,
	});
}

export function useLookupOrgBySlug(slug: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["org-slug", slug],
		queryFn: () => gqlFetch(OrganizationLookupBySlugDocument, { slug }),
		enabled: slug.length >= 3,
	});
}

export function useClaimOrganization() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ name, slug }: { name: string; slug: string }) =>
			gqlFetch(ClaimOrganizationDocument, { input: { name, slug } }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
	});
}

/** Redeems the claim token the AWS Marketplace fulfillment endpoint hands to `/aws-mktp`. */
export function useLinkAWSMarketplaceSubscription() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: (claimToken: string) =>
			gqlFetch(LinkAwsMarketplaceSubscriptionDocument, { claimToken }),
	});
}

/** The org's Stripe subscription (null = never subscribed). Written only by the Stripe webhook. */
export function useOrganizationSubscription() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["organizationSubscription"],
		queryFn: async () =>
			(await gqlFetch(GetOrganizationSubscriptionDocument))
				.organizationSubscription ?? null,
	});
}

/** The org's AWS Marketplace subscription (null = never linked), as entitled at its last fulfillment. */
export function useOrganizationAWSMarketplaceSubscription() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["organizationAWSMarketplaceSubscription"],
		queryFn: async () =>
			(await gqlFetch(GetOrganizationAwsMarketplaceSubscriptionDocument))
				.organizationAWSMarketplaceSubscription ?? null,
	});
}

/** Starts Stripe Checkout (org admins only) and sends the browser to the hosted page. */
export function useStartStripeCheckout() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: async () =>
			(await gqlFetch(CreateStripeCheckoutSessionDocument))
				.createStripeCheckoutSession.url,
		onSuccess: (url) => window.location.assign(url),
	});
}

/** Opens the Stripe Billing Portal (update card, cancel) for the org. */
export function useOpenStripeBillingPortal() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: async () =>
			(await gqlFetch(CreateStripeBillingPortalSessionDocument))
				.createStripeBillingPortalSession.url,
		onSuccess: (url) => window.location.assign(url),
	});
}

export function useOrganization() {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["organization"],
		queryFn: async () => {
			const data = await gqlFetch(GetOrganizationDocument);
			return (data.organization as Organization) ?? null;
		},
	});
}

/** The authenticated org's slug — used as the `$orgSlug` URL segment. */
export function useOrgSlug(): string | undefined {
	const { data: org } = useOrganization();
	// `organization` is a single-element list; the user has exactly one org.
	return org?.[0]?.slug ?? undefined;
}

/** The `$orgSlug` from the current route, falling back to the user's own org slug. */
export function useRouteOrgSlug(): string | undefined {
	const params = useParams({ strict: false }) as { orgSlug?: string };
	const mySlug = useOrgSlug();
	return params.orgSlug ?? mySlug;
}

/**
 * Aggregated org metrics for the admin dashboard: user/workspace/task counts, resource caps, and
 * current-month token usage.
 */
export function useOrganizationMetrics() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["organizationMetrics"],
		queryFn: async () => {
			const data = await gqlFetch(GetOrganizationMetricsDocument);
			return data.organizationMetrics;
		},
	});
}

export function useOrgAdmins() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["orgAdmins"],
		queryFn: async () => {
			const data = await gqlFetch(GetOrganizationAdminsDocument);
			return data.organizationAdmins ?? [];
		},
	});
}

/** Returns true when the currently authenticated user is an org admin. */
export function useIsOrgAdmin() {
	const me = useMe();
	const orgAdmins = useOrgAdmins();

	const isAdmin =
		!!me.data?.id &&
		(orgAdmins.data?.some((a) => a.userId === me.data?.id) ?? false);

	return {
		isAdmin,
		isPending: me.isPending || orgAdmins.isPending,
	};
}

export function useAddOrgAdmin() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (userId: string) => {
			const data = await gqlFetch(AddOrganizationAdminDocument, { userId });
			return data.addOrganizationAdmin.organizationAdmin;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["orgAdmins"] });
		},
	});
}

export function useRemoveOrgAdmin() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (userId: string) => {
			await gqlFetch(RemoveOrganizationAdminDocument, { userId });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["orgAdmins"] });
		},
	});
}

export function useUpdateOrganization() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (name: string) => {
			const data = await gqlFetch(UpdateOrganizationDocument, {
				input: { name },
			});
			return data.updateOrganization.organization;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["organization"] });
		},
	});
}

// ─── Domains ─────────────────────────────────────────────────────────────────

export function useOrgDomains() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["orgDomains"],
		queryFn: async () => {
			const data = await gqlFetch(GetOrganizationDomainsDocument);
			return data.organizationDomains ?? [];
		},
	});
}

export function useAddOrgDomain() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (domain: string) => {
			const data = await gqlFetch(AddOrganizationDomainDocument, { domain });
			return data.addOrganizationDomain.organizationDomain;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["orgDomains"] }),
	});
}

export function useRemoveOrgDomain() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (domainId: string) => {
			await gqlFetch(RemoveOrganizationDomainDocument, { domainId });
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["orgDomains"] }),
	});
}

export function useRequestDomainVerification() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (domainId: string) => {
			const data = await gqlFetch(
				RequestOrganizationDomainVerificationDocument,
				{
					domainId,
				},
			);
			return data.requestOrganizationDomainVerification.organizationDomain;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["orgDomains"] }),
	});
}

export function useVerifyDomain() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (domainId: string) => {
			const data = await gqlFetch(VerifyOrganizationDomainDocument, {
				domainId,
			});
			return data.verifyOrganizationDomain.organizationDomain;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["orgDomains"] }),
	});
}
